import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Reveal } from '../../components/shared/reveal/reveal';
import { listPosts } from '../../components/shared/blog/blog-api';
import { formatDate } from '../../components/shared/blog/format-date';
import { isBlogConfigured } from '../../config';
import type { PostSummary } from '../../components/shared/blog/contracts/post';

type LoadState = 'idle' | 'loading' | 'loaded' | 'error';

export function BlogIndexPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tag = searchParams.get('tag') ?? undefined;

  const [posts, setPosts] = useState<PostSummary[]>([]);
  const [cursor, setCursor] = useState<string | undefined>();
  const [state, setState] = useState<LoadState>('idle');
  const [loadingMore, setLoadingMore] = useState(false);
  // A failed "load more" surfaces inline (below) rather than tearing down the
  // already-loaded grid, unlike an initial-load failure (`state === 'error'`).
  const [loadMoreError, setLoadMoreError] = useState(false);

  // The controller for the in-flight request; starting any load aborts the
  // previous one so a stale append can't land on a freshly-filtered list.
  const inFlight = useRef<AbortController | null>(null);

  useEffect(() => {
    document.title = tag ? `Blog · #${tag} — Nick Gardner` : 'Blog — Nick Gardner';
  }, [tag]);

  // `append` carries the cursor as an explicit arg so this callback need not
  // close over `cursor` (avoids a stale-closure reload after setCursor).
  const load = useCallback(
    (opts: { appendCursor?: string } = {}) => {
      if (!isBlogConfigured()) {
        setState('loaded');
        setPosts([]);
        return;
      }
      const append = opts.appendCursor !== undefined;
      inFlight.current?.abort();
      const controller = new AbortController();
      inFlight.current = controller;

      if (append) {
        setLoadingMore(true);
        setLoadMoreError(false);
      } else {
        setState('loading');
      }
      listPosts({ tag, cursor: opts.appendCursor }, controller.signal)
        .then((res) => {
          setPosts((prev) => (append ? [...prev, ...res.items] : res.items));
          setCursor(res.nextCursor);
          setState('loaded');
          setLoadingMore(false);
        })
        .catch((err) => {
          if (err?.name === 'AbortError') return; // superseded — leave state alone
          // A load-more failure keeps the existing list; only an initial-load
          // failure replaces the page with the full error state.
          if (append) {
            setLoadMoreError(true);
            setLoadingMore(false);
          } else {
            setState('error');
          }
        });
    },
    [tag],
  );

  // Reload from scratch whenever the tag filter changes (or on first mount).
  useEffect(() => {
    setCursor(undefined);
    load();
    return () => inFlight.current?.abort();
  }, [load]);

  return (
    <main id="main" className="mx-auto max-w-content px-5 py-16">
      <Reveal>
        <p className="section-kicker">{'// blog'}</p>
        <h1 className="mt-2 font-mono text-3xl font-bold tracking-tight text-fg sm:text-4xl">
          Writing
        </h1>
        <p className="mt-3 max-w-prose text-muted">
          Notes on what I&apos;m building — web, hardware, and the AWS glue between them.
        </p>
      </Reveal>

      {tag && (
        <div className="mt-6 flex items-center gap-3 font-mono text-sm">
          <span className="text-muted">filtering by</span>
          <span className="tag">#{tag}</span>
          <button
            type="button"
            onClick={() => setSearchParams({})}
            className="text-muted underline-offset-4 hover:text-accent hover:underline"
          >
            clear
          </button>
        </div>
      )}

      <div className="mt-10">
        {state === 'loading' && <SkeletonGrid />}

        {state === 'error' && <ErrorState onRetry={() => load()} />}

        {state === 'loaded' && posts.length === 0 && <EmptyState tag={tag} />}

        {state === 'loaded' && posts.length > 0 && (
          <>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {posts.map((post, i) => (
                <Reveal key={post.slug} delay={(i % 3) * 80}>
                  <PostCard post={post} />
                </Reveal>
              ))}
            </div>

            {cursor && (
              <div className="mt-10 text-center">
                <button
                  type="button"
                  onClick={() => load({ appendCursor: cursor })}
                  disabled={loadingMore}
                  className="tag disabled:opacity-50"
                >
                  {loadingMore ? 'loading…' : loadMoreError ? 'retry' : 'load more'}
                </button>
                {loadMoreError && (
                  <p role="alert" className="mt-3 font-mono text-sm text-muted">
                    Couldn&apos;t load more posts.
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}

function PostCard({ post }: { post: PostSummary }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="terminal-card group flex h-full flex-col p-5 transition-all duration-200 hover:-translate-y-1 hover:border-accent/60"
    >
      {post.publishedAt && (
        <span className="font-mono text-xs tabular-nums text-muted">
          {formatDate(post.publishedAt)}
        </span>
      )}
      <h2 className="mt-2 font-mono text-lg font-medium text-fg group-hover:text-accent">
        {post.title}
      </h2>
      {post.excerpt && (
        <p className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-muted">
          {post.excerpt}
        </p>
      )}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {post.tags.map((t) => (
            <span key={t} className="font-mono text-[11px] text-muted">
              #{t}
            </span>
          ))}
        </div>
      )}
    </Link>
  );
}

function SkeletonGrid() {
  return (
    <div
      className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"
      aria-hidden
      role="presentation"
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="terminal-card h-40 animate-pulse p-5">
          <div className="h-3 w-20 rounded bg-elevated" />
          <div className="mt-3 h-4 w-3/4 rounded bg-elevated" />
          <div className="mt-4 h-3 w-full rounded bg-elevated" />
          <div className="mt-2 h-3 w-5/6 rounded bg-elevated" />
        </div>
      ))}
    </div>
  );
}

function EmptyState({ tag }: { tag?: string }) {
  return (
    <div className="terminal-card px-6 py-16 text-center">
      <p className="font-mono text-sm text-accent">{'// empty'}</p>
      <p className="mt-2 text-muted">
        {tag ? `No posts tagged #${tag} yet.` : 'No posts published yet — check back soon.'}
      </p>
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="terminal-card px-6 py-16 text-center" role="alert">
      <p className="font-mono text-sm text-accent">{'// error'}</p>
      <p className="mt-2 text-muted">Couldn&apos;t load posts.</p>
      <button type="button" onClick={onRetry} className="tag mt-6">
        retry
      </button>
    </div>
  );
}
